import {
  actionToUci,
  decodeAction,
  encodeAction,
} from "./action";
import {
  ACTION_SPACE,
  MAX_STEPS,
  Piece,
  type ChessState,
  type Color,
  type TerminalReason,
} from "./types";
import type { GameEnvironment } from "../game/types";

const INITIAL_BOARD = Int8Array.from([
  4, 1, 0, 0, 0, 0, -1, -4, 2, 1, 0, 0, 0, 0, -1, -2,
  3, 1, 0, 0, 0, 0, -1, -3, 5, 1, 0, 0, 0, 0, -1, -5,
  6, 1, 0, 0, 0, 0, -1, -6, 3, 1, 0, 0, 0, 0, -1, -3,
  2, 1, 0, 0, 0, 0, -1, -2, 4, 1, 0, 0, 0, 0, -1, -4,
]);

function flipSquare(square: number): number {
  return square < 0 ? -1 : Math.floor(square / 8) * 8 + 7 - square % 8;
}

function flipBoard(board: Int8Array): Int8Array {
  const result = new Int8Array(64);
  for (let square = 0; square < 64; square += 1) {
    result[flipSquare(square)] = -(board[square] ?? 0);
  }
  return result;
}

function positionKey(
  board: Int8Array,
  color: Color,
  rights: readonly boolean[],
  enPassant: number,
): string {
  return `${color}|${Array.from(board).join(",")}|${rights.map(Number).join("")}|${enPassant}`;
}

function attacked(board: Int8Array, square: number): boolean {
  const rank = square % 8;
  const file = Math.floor(square / 8);
  // Opponent pawns move toward decreasing canonical ranks.
  for (const dc of [-1, 1]) {
    const f = file + dc;
    const r = rank + 1;
    if (f >= 0 && f < 8 && r < 8 && board[f * 8 + r] === -Piece.Pawn) return true;
  }
  for (const [dr, dc] of [
    [-1, -2], [1, -2], [-2, -1], [2, -1],
    [-1, 2], [1, 2], [-2, 1], [2, 1],
  ] as const) {
    const r = rank + dr;
    const f = file + dc;
    if (r >= 0 && r < 8 && f >= 0 && f < 8 && board[f * 8 + r] === -Piece.Knight) return true;
  }
  for (let dr = -1; dr <= 1; dr += 1) {
    for (let dc = -1; dc <= 1; dc += 1) {
      if (dr === 0 && dc === 0) continue;
      const r = rank + dr;
      const f = file + dc;
      if (r >= 0 && r < 8 && f >= 0 && f < 8 && board[f * 8 + r] === -Piece.King) return true;
    }
  }
  const rays = [
    [1, 0, Piece.Rook], [-1, 0, Piece.Rook], [0, 1, Piece.Rook], [0, -1, Piece.Rook],
    [1, 1, Piece.Bishop], [1, -1, Piece.Bishop], [-1, 1, Piece.Bishop], [-1, -1, Piece.Bishop],
  ] as const;
  for (const [dr, dc, piece] of rays) {
    for (let distance = 1; distance < 8; distance += 1) {
      const r = rank + dr * distance;
      const f = file + dc * distance;
      if (r < 0 || r >= 8 || f < 0 || f >= 8) break;
      const occupant = board[f * 8 + r] ?? 0;
      if (occupant === 0) continue;
      if (occupant === -piece || occupant === -Piece.Queen) return true;
      break;
    }
  }
  return false;
}

export function isChecked(state: Pick<ChessState, "board">): boolean {
  const king = state.board.indexOf(Piece.King);
  return king < 0 || attacked(state.board, king);
}

function applyCanonical(
  state: Pick<ChessState, "board" | "enPassant" | "castlingRights">,
  label: number,
): { board: Int8Array; captured: boolean; pawn: boolean; rights: [boolean, boolean, boolean, boolean]; enPassant: number } {
  const action = decodeAction(label);
  if (action.to < 0) throw new RangeError("Action ends off board");
  const board = state.board.slice();
  let piece = board[action.from] ?? 0;
  const pawn = piece === Piece.Pawn;
  const enPassantCapture = pawn && action.to === state.enPassant;
  const captured = (board[action.to] ?? 0) < 0 || enPassantCapture;
  if (enPassantCapture) board[action.to - 1] = Piece.Empty;

  if (piece === Piece.King && action.from === 32 && action.to === 16) {
    board[0] = Piece.Empty;
    board[24] = Piece.Rook;
  } else if (piece === Piece.King && action.from === 32 && action.to === 48) {
    board[56] = Piece.Empty;
    board[40] = Piece.Rook;
  }
  const rights: [boolean, boolean, boolean, boolean] = [...state.castlingRights];
  if (action.from === 32) {
    rights[0] = false;
    rights[1] = false;
  }
  if (action.from === 0) rights[0] = false;
  if (action.from === 56) rights[1] = false;
  if (action.to === 7) rights[2] = false;
  if (action.to === 63) rights[3] = false;

  if (pawn && action.from % 8 === 6) {
    piece = action.underpromotion < 0
      ? Piece.Queen
      : [Piece.Rook, Piece.Bishop, Piece.Knight][action.underpromotion]!;
  }
  board[action.from] = Piece.Empty;
  board[action.to] = piece;
  return {
    board,
    captured,
    pawn,
    rights,
    enPassant: pawn && Math.abs(action.to - action.from) === 2
      ? (action.to + action.from) / 2
      : -1,
  };
}

function pseudoActions(state: ChessState): number[] {
  const actions: number[] = [];
  const add = (from: number, to: number, underpromotion: -1 | 0 | 1 | 2 = -1) => {
    if (to < 0 || to >= 64) return;
    const occupant = state.board[to] ?? 0;
    if (occupant > 0) return;
    if (from % 8 === 6 && state.board[from] === Piece.Pawn) {
      if (underpromotion < 0) {
        actions.push(encodeAction({ from, to, underpromotion: -1 }));
        for (const promotion of [0, 1, 2] as const) {
          actions.push(encodeAction({ from, to, underpromotion: promotion }));
        }
      }
      return;
    }
    actions.push(encodeAction({ from, to, underpromotion }));
  };
  for (let from = 0; from < 64; from += 1) {
    const piece = state.board[from] ?? 0;
    if (piece <= 0) continue;
    const rank = from % 8;
    const file = Math.floor(from / 8);
    if (piece === Piece.Pawn) {
      const one = from + 1;
      if (rank < 7 && state.board[one] === Piece.Empty) {
        add(from, one);
        if (rank === 1 && state.board[from + 2] === Piece.Empty) add(from, from + 2);
      }
      for (const dc of [-1, 1]) {
        const f = file + dc;
        const to = f * 8 + rank + 1;
        if (f >= 0 && f < 8 && rank < 7 && ((state.board[to] ?? 0) < 0 || to === state.enPassant)) {
          add(from, to);
        }
      }
      continue;
    }
    if (piece === Piece.Knight) {
      for (const [dr, dc] of [
        [-1, -2], [1, -2], [-2, -1], [2, -1],
        [-1, 2], [1, 2], [-2, 1], [2, 1],
      ] as const) {
        const r = rank + dr;
        const f = file + dc;
        if (r >= 0 && r < 8 && f >= 0 && f < 8) add(from, f * 8 + r);
      }
      continue;
    }
    if (piece === Piece.King) {
      for (let dr = -1; dr <= 1; dr += 1) {
        for (let dc = -1; dc <= 1; dc += 1) {
          if (dr === 0 && dc === 0) continue;
          const r = rank + dr;
          const f = file + dc;
          if (r >= 0 && r < 8 && f >= 0 && f < 8) add(from, f * 8 + r);
        }
      }
      const safe = !attacked(state.board, 32);
      if (
        state.castlingRights[0] && safe &&
        state.board[0] === Piece.Rook &&
        state.board[8] === 0 && state.board[16] === 0 && state.board[24] === 0 &&
        !attacked(state.board, 24) && !attacked(state.board, 16)
      ) add(32, 16);
      if (
        state.castlingRights[1] && safe &&
        state.board[56] === Piece.Rook &&
        state.board[40] === 0 && state.board[48] === 0 &&
        !attacked(state.board, 40) && !attacked(state.board, 48)
      ) add(32, 48);
      continue;
    }
    const directions = piece === Piece.Bishop
      ? [[1, 1], [1, -1], [-1, 1], [-1, -1]]
      : piece === Piece.Rook
        ? [[1, 0], [-1, 0], [0, 1], [0, -1]]
        : [[1, 1], [1, -1], [-1, 1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dr, dc] of directions) {
      for (let distance = 1; distance < 8; distance += 1) {
        const r = rank + (dr ?? 0) * distance;
        const f = file + (dc ?? 0) * distance;
        if (r < 0 || r >= 8 || f < 0 || f >= 8) break;
        const to = f * 8 + r;
        const occupant = state.board[to] ?? 0;
        if (occupant > 0) break;
        add(from, to);
        if (occupant < 0) break;
      }
    }
  }
  return actions;
}

export function legalActions(state: ChessState): number[] {
  if (state.terminated || state.truncated) return [];
  return pseudoActions(state).filter((action) => !isChecked({ board: applyCanonical(state, action).board }));
}

function insufficient(board: Int8Array): boolean {
  const pieces = Array.from(board).filter(Boolean);
  const heavy = pieces.filter((piece) =>
    Math.abs(piece) === Piece.Pawn ||
    Math.abs(piece) === Piece.Rook ||
    Math.abs(piece) === Piece.Queen
  );
  if (pieces.length <= 2) return true;
  if (pieces.length === 3 && heavy.length === 0) return true;
  if (heavy.length > 0 || pieces.some((piece) => Math.abs(piece) === Piece.Knight)) return false;
  const bishops: number[] = [];
  board.forEach((piece, square) => {
    if (Math.abs(piece) === Piece.Bishop) bishops.push(square);
  });
  return pieces.length === bishops.length + 2 &&
    bishops.every((square) => (Math.floor(square / 8) + square % 8) % 2 ===
      (Math.floor(bishops[0]! / 8) + bishops[0]! % 8) % 2);
}

function terminalStatus(state: ChessState, legal: number[]): {
  reason: TerminalReason | null;
  terminated: boolean;
  truncated: boolean;
  rewards: [number, number];
} {
  let reason: TerminalReason | null = null;
  let truncated = false;
  if (legal.length === 0) reason = isChecked(state) ? "checkmate" : "stalemate";
  else if (state.halfmoveCount >= 100) reason = "fifty-move";
  else if (insufficient(state.board)) reason = "insufficient-material";
  else {
    const current = positionKey(state.board, state.color, state.castlingRights, state.enPassant);
    if (state.positionHistory.filter((key) => key === current).length >= 3) {
      reason = "threefold-repetition";
    } else if (state.stepCount >= MAX_STEPS) {
      reason = "max-steps";
      truncated = true;
    }
  }
  const rewards: [number, number] = [0, 0];
  if (reason === "checkmate") {
    rewards[state.color] = -1;
    rewards[(1 - state.color) as Color] = 1;
  }
  return { reason, terminated: reason !== null && !truncated, truncated, rewards };
}

export function adjudicate(state: ChessState): ChessState {
  const history = state.positionHistory.length > 0
    ? state.positionHistory
    : [positionKey(state.board, state.color, state.castlingRights, state.enPassant)];
  const normalized = history === state.positionHistory
    ? state
    : { ...state, positionHistory: history };
  const status = terminalStatus(normalized, legalActions(normalized));
  return { ...normalized, ...status, terminalReason: status.reason };
}

function baseState(
  board: Int8Array,
  color: Color,
  rights: [boolean, boolean, boolean, boolean],
  enPassant: number,
  halfmoveCount: number,
  fullmoveCount: number,
): ChessState {
  const state: ChessState = {
    color,
    board,
    boardHistory: [board.slice()],
    castlingRights: rights,
    enPassant,
    halfmoveCount,
    fullmoveCount,
    stepCount: 0,
    positionHistory: [positionKey(board, color, rights, enPassant)],
    moves: [],
    terminated: false,
    truncated: false,
    terminalReason: null,
    rewards: [0, 0],
  };
  const status = terminalStatus(state, pseudoActions(state).filter((action) =>
    !isChecked({ board: applyCanonical(state, action).board })
  ));
  return { ...state, ...status, terminalReason: status.reason };
}

export class PgxChess implements GameEnvironment<ChessState> {
  init(): ChessState {
    return baseState(INITIAL_BOARD.slice(), 0, [true, true, true, true], -1, 0, 1);
  }

  step(state: ChessState, action: number): ChessState {
    const legal = legalActions(state);
    if (!legal.includes(action)) {
      const rewards: [number, number] = [0, 0];
      rewards[state.color] = -1;
      rewards[(1 - state.color) as Color] = 1;
      return {
        ...state,
        terminated: true,
        terminalReason: "illegal-action",
        rewards,
      };
    }
    const applied = applyCanonical(state, action);
    const color = (1 - state.color) as Color;
    const board = flipBoard(applied.board);
    const rights: [boolean, boolean, boolean, boolean] = [
      applied.rights[2], applied.rights[3], applied.rights[0], applied.rights[1],
    ];
    const enPassant = flipSquare(applied.enPassant);
    const boardHistory = [
      board.slice(),
      ...state.boardHistory.map(flipBoard),
    ].slice(0, 8);
    const key = positionKey(board, color, rights, enPassant);
    const next: ChessState = {
      color,
      board,
      boardHistory,
      castlingRights: rights,
      enPassant,
      halfmoveCount: applied.captured || applied.pawn ? 0 : state.halfmoveCount + 1,
      fullmoveCount: state.fullmoveCount + (state.color === 1 ? 1 : 0),
      stepCount: state.stepCount + 1,
      positionHistory: [...state.positionHistory, key],
      moves: [...state.moves, { action, uci: actionToUci(action, state.color) }],
      terminated: false,
      truncated: false,
      terminalReason: null,
      rewards: [0, 0],
    };
    const status = terminalStatus(next, pseudoActions(next).filter((candidate) =>
      !isChecked({ board: applyCanonical(next, candidate).board })
    ));
    return { ...next, ...status, terminalReason: status.reason };
  }

  legalActionMask(state: ChessState): Uint8Array {
    const mask = new Uint8Array(ACTION_SPACE);
    for (const action of legalActions(state)) mask[action] = 1;
    return mask;
  }

  observe(state: ChessState): Float32Array {
    const observation = new Float32Array(8 * 8 * 119);
    for (let history = 0; history < 8; history += 1) {
      const board = state.boardHistory[history] ?? new Int8Array(64);
      const historyKey = state.positionHistory[state.positionHistory.length - 1 - history];
      const repetitions = historyKey !== undefined
        ? state.positionHistory.filter((key) => key === historyKey).length - 1
        : 0;
      for (let outRank = 0; outRank < 8; outRank += 1) {
        for (let outFile = 0; outFile < 8; outFile += 1) {
          // numpy.rot90(board.reshape(8, 8), k=1)[outRank, outFile]
          const piece = board[outFile * 8 + (7 - outRank)] ?? 0;
          const base = (outRank * 8 + outFile) * 119 + history * 14;
          if (piece > 0) observation[base + piece - 1] = 1;
          else if (piece < 0) observation[base + 6 + -piece - 1] = 1;
          observation[base + 12] = repetitions <= 0 ? 1 : 0;
          observation[base + 13] = repetitions >= 1 ? 1 : 0;
        }
      }
    }
    for (let square = 0; square < 64; square += 1) {
      const base = square * 119 + 112;
      observation[base] = state.color;
      observation[base + 1] = Math.min(state.stepCount / MAX_STEPS, 1);
      for (let right = 0; right < 4; right += 1) {
        observation[base + 2 + right] = state.castlingRights[right] ? 1 : 0;
      }
      // JAX lowers float32 division by this constant to multiplication by a
      // float32 reciprocal; preserve that last-bit behavior for byte parity.
      observation[base + 6] = Math.fround(
        Math.fround(state.halfmoveCount) * Math.fround(1 / 100),
      );
    }
    return observation;
  }

  currentPlayer(state: ChessState): Color {
    return state.color;
  }

  isTerminal(state: ChessState): boolean {
    return state.terminated || state.truncated;
  }

  terminalValue(state: ChessState): number {
    return state.rewards[state.color] ?? 0;
  }

  formatAction(state: ChessState, action: number): string {
    return actionToUci(action, state.color);
  }
}

export const chess = new PgxChess();
